/**
 * The Inference Descent — M1 Template
 *
 * A reimagining of the Ladder of Inference as a descent into constructed reality.
 * Reads top-to-bottom: ground floor (observable data) → basement (action).
 * The deeper you go, the more certain you feel — with less actual evidence.
 *
 * Based on: Chris Argyris' Ladder of Inference
 * Origin: Action Science · Double-Loop Learning Theory
 * Popularized: Peter Senge, The Fifth Discipline (1990)
 * Peak Priorities Encoding: cf.ladder.inference · LOI
 */

import React from 'react';
import {
  Eye, Filter, Brain, Lightbulb, CheckCircle, Heart, Zap,
  ArrowDown, RotateCcw, ArrowRight, ArrowUp,
  BookOpen, ChevronRight
} from 'lucide-react';

// ─── Level Data (ordered 1→7, ground floor to basement) ──────────────────────

const LEVELS = [
  {
    id: 1, title: 'Observable Data', floor: 'Ground Floor', depth: '0',
    icon: <Eye className="h-4 w-4" />,
    zone: 'surface',
    tagline: 'What a camera could capture — unfiltered reality',
    description: 'The only level where reality lives undisturbed. Observable facts, timestamps, words actually spoken, things a recording could capture. No emotion, no meaning, no interpretation — yet.',
    inquiry: 'What actually happened? What could I point to that a camera could capture?',
    doExample: '"At the Tuesday standup, after I described the revised handoff process, Marcus said nothing. He glanced at his phone. The meeting moved on. That is everything that happened."',
    dontExample: '"She was dismissive." — This is already two levels deeper. You\'ve descended without noticing.',
    svgColor: '#BAE6FD', textColor: '#0C4A6E',
    bg: 'bg-[var(--slate-dim)]', border: 'border-[var(--slate)]', text: 'text-[var(--text-sec)]', pill: 'bg-[var(--slate)]',
  },
  {
    id: 2, title: 'Select', floor: 'Level B1', depth: '−1',
    icon: <Filter className="h-4 w-4" />,
    zone: 'surface',
    tagline: "We notice a fraction of what's there",
    description: 'You step underground. From all available data, you choose what to pay attention to — and what to ignore. This feels neutral but it\'s not. Your existing beliefs are already shaping your selection. This is where the reflexive loop re-enters.',
    inquiry: 'What data am I focusing on? What am I not focusing on, and why?',
    doExample: '"I\'m focusing on the silence and the phone glance. I\'m not noticing that two other teammates also said nothing, or that Marcus nodded at one point while I was speaking."',
    dontExample: '"I just noticed what happened." — Selection always occurs. Claiming otherwise is the error.',
    svgColor: '#7DD3FC', textColor: '#0369A1',
    bg: 'bg-[var(--slate-dim)]', border: 'border-[var(--slate)]', text: 'text-[var(--text-sec)]', pill: 'bg-[var(--slate)]',
  },
  {
    id: 3, title: 'Interpret', floor: 'Level B2', depth: '−2',
    icon: <Brain className="h-4 w-4" />,
    zone: 'underground',
    tagline: 'We add meaning the data doesn\'t contain',
    description: 'You add meaning to what you selected. This happens so fast it feels like reading, not writing. The same word — "noted" — means dismissal to one person and acknowledgment to another. Neither is lying. Both are interpreting.',
    inquiry: 'What meaning am I adding? Could someone else look at the same data and add different meaning?',
    doExample: '"I\'m reading the silence as disapproval. A colleague might read it as distraction, exhaustion, or simply being in listen mode. Neither is in the data."',
    dontExample: '"She was clearly uninterested." — The word "clearly" is doing a lot of work. It wasn\'t in the data.',
    svgColor: '#5EEAD4', textColor: '#134E4A',
    bg: 'bg-[var(--green-dim)]', border: 'border-[var(--green)]', text: 'text-[var(--green)] dark:text-[var(--text-ter)]', pill: 'bg-[var(--green)]',
  },
  {
    id: 4, title: 'Assume', floor: 'Level B3', depth: '−3',
    icon: <Lightbulb className="h-4 w-4" />,
    zone: 'underground',
    tagline: 'Prior experience fills the gap',
    description: 'Your interpretations aren\'t free-floating — they attach to assumptions you already hold. You assume that short replies mean low interest. You learned this from someone else, somewhere else. You\'ve carried it here without declaring it.',
    inquiry: 'What am I assuming? Where does this assumption come from — and does it actually apply here?',
    doExample: '"I\'m assuming fast replies = high priority. I formed this belief with my previous manager, who was very effusive."',
    dontExample: '"Anyone would assume that." — Assumptions feel universal. They\'re always personal.',
    svgColor: '#86EFAC', textColor: '#14532D',
    bg: 'bg-[var(--green-dim)]', border: 'border-[var(--green)]', text: 'text-[var(--green)] dark:text-[var(--text-ter)]', pill: 'bg-[var(--green)]',
  },
  {
    id: 5, title: 'Conclude', floor: 'Level B4', depth: '−4',
    icon: <CheckCircle className="h-4 w-4" />,
    zone: 'deep',
    tagline: 'Feels obvious — is built on three prior levels',
    description: 'You draw a conclusion. It feels self-evident because it follows logically from Levels B1–B3. But those levels were filled by you — with selected data, added meaning, and borrowed assumptions. The inference gap between Level 0 and here can be enormous, and it\'s invisible from inside.',
    inquiry: 'What conclusion am I drawing? What\'s the actual gap between my observable data and this conclusion?',
    doExample: '"My conclusion: Marcus doesn\'t support this direction. But the inference gap from a phone glance to that conclusion is enormous."',
    dontExample: '"It\'s obvious what\'s going on." — The more obvious it feels, the wider the gap usually is.',
    svgColor: '#FDE68A', textColor: '#78350F',
    bg: 'bg-[var(--mustard-dim)]', border: 'border-[var(--mustard)]', text: 'text-[var(--mustard)]', pill: 'bg-[var(--mustard)]',
  },
  {
    id: 6, title: 'Believe', floor: 'Level B5', depth: '−5',
    icon: <Heart className="h-4 w-4" />,
    zone: 'deep',
    tagline: 'Conclusions harden — and reach back up to the surface',
    description: 'Your conclusion becomes a belief. Beliefs operate below conscious awareness. They feel like facts. And this is where the reflexive loop activates: your new belief travels back up to Level B1 and begins filtering what data you choose to notice. The loop closes.',
    inquiry: 'What do I now believe as a result of this? How is this belief shaping what I\'ll notice next?',
    doExample: '"I now believe Marcus has been quietly skeptical of my judgment for months. That belief will make me more alert to his silences and less likely to notice when he\'s actually engaged."',
    dontExample: '"I believe it because it\'s true." — All beliefs feel like facts from the inside.',
    svgColor: '#FCD34D', textColor: '#92400E',
    bg: 'bg-[var(--mustard-dim)]', border: 'border-[var(--mustard)]', text: 'text-[var(--mustard)]', pill: 'bg-[var(--mustard)]',
  },
  {
    id: 7, title: 'Act', floor: 'The Basement', depth: '−6',
    icon: <Zap className="h-4 w-4" />,
    zone: 'deep',
    tagline: 'Completely rational — built on constructed reality',
    description: 'You act. Withdraw. Vent. Escalate. Plan your exit. Your action is perfectly logical given your beliefs. Your beliefs were built from selected, interpreted, assumed, concluded data — all added by you, all invisible to you. This is why the basement feels safe. You have complete justification.',
    inquiry: 'What am I about to do? If my belief is wrong, what\'s the cost of this action?',
    doExample: '"I\'m routing conversations around Marcus and telling peers he\'s resistant to change. Before I go further: what if I\'m wrong about the belief this is based on?"',
    dontExample: '"I just did what made sense." — Actions always make sense at the basement level. That\'s the problem.',
    svgColor: '#FB923C', textColor: '#9A3412',
    bg: 'bg-[var(--coral-dim)]', border: 'border-[var(--coral)]', text: 'text-[var(--coral)]', pill: 'bg-[var(--coral)]',
  },
];

const ZONES = [
  {
    id: 'surface',
    label: 'SURFACE',
    sublabel: 'Observable Reality',
    bg: 'bg-[var(--slate-dim)]', border: 'border-[var(--slate)]', color: 'text-[var(--text-sec)]',
    desc: 'Reality lives here. You can point to everything on the surface. When you\'re in conflict, this is where you return.',
  },
  {
    id: 'underground',
    label: 'DOWN THE STAIRS',
    sublabel: 'Meaning Made',
    bg: 'bg-[var(--green-dim)]', border: 'border-[var(--green)]', color: 'text-[var(--green)] dark:text-[var(--text-ter)]',
    desc: 'The moment you step off the ground floor, meaning gets added automatically. Most people don\'t notice this happening. That\'s the problem.',
  },
  {
    id: 'deep',
    label: 'THE BASEMENT',
    sublabel: 'Certainty Without Evidence',
    bg: 'bg-[var(--mustard-dim)]', border: 'border-[var(--mustard)]', color: 'text-[var(--mustard)]',
    desc: 'You\'ve arrived. This is where you feel most certain and are furthest from observable reality. The reflexive loop lives here.',
  },
];


// ─── Main Component ───────────────────────────────────────────────────────────

export const LadderOfInferenceM1: React.FC = () => {
  const [expanded, setExpanded] = React.useState<Set<number>>(new Set());
  const toggleExpanded = (id: number) =>
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  return (
    <div className="space-y-8">

      {/* ── Attribution Banner ─────────────────────────────────────────────── */}
      <div className="bg-charcoal-900 text-white px-6 py-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <BookOpen className="h-5 w-5 text-[var(--mustard)] flex-shrink-0" />
          <div>
            <span className="text-sm font-medium text-white">Based on </span>
            <span className="text-sm font-bold text-[var(--mustard)]">Chris Argyris'</span>
            <span className="text-sm text-[var(--text-ter)]"> Ladder of Inference · Action Science · Double-Loop Learning</span>
          </div>
        </div>
        <div className="text-xs text-[var(--text-ter)] italic">
          Extended by Peter Senge · <em>The Fifth Discipline</em> (1990)
        </div>
      </div>

      {/* ── Hero ───────────────────────────────────────────────────────────── */}
      <div className="space-y-4">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 bg-[var(--slate-dim)] flex items-center justify-center flex-shrink-0">
            <ArrowDown className="h-8 w-8 text-[var(--text-ter)]" />
          </div>
          <div className="flex-1">
            <h1 className="text-4xl font-bold text-charcoal-900 leading-tight">The Inference Descent</h1>
            <p className="text-charcoal-500 text-sm mt-1">
              Based on Chris Argyris' Ladder of Inference
            </p>
          </div>
        </div>

        {/* Core phrase */}
        <div className="bg-charcoal-900 p-5">
          <div className="text-xs font-bold text-[var(--mustard)] uppercase tracking-widest mb-2">📌 Core Phrase</div>
          <p className="text-xl font-semibold text-white leading-snug">
            "We see what we believe — not just what's there."
          </p>
          <p className="text-sm text-[var(--text-ter)] mt-2 leading-relaxed">
            The central insight from <strong className="text-[var(--text-ter)]">Chris Argyris</strong>: our beliefs
            don't follow from what we observe — they actively filter what we choose to observe.
            The deeper we descend from observable reality, the more certain we feel, and the less
            connected we are to what actually happened.
          </p>
        </div>

        {/* One-sentence definition */}
        <div className="bg-[var(--slate-dim)] border-l-4 border-[var(--slate)] p-5">
          <p className="text-base text-charcoal-800 leading-relaxed">
            <strong>The Inference Descent</strong> maps the automatic journey from observable data —
            ground-level reality — down through interpretation, assumption, and conclusion into the
            basement of belief and action. The further you descend, the more certain everything feels,
            and the further you are from what actually happened.
          </p>
        </div>
      </div>

      {/* ── Level list — one continuous path downward ──────────────────────── */}
      <div className="border-2 border-[var(--rule)] overflow-hidden divide-y divide-[var(--rule)]">
        {ZONES.map((zone) => (
          <div key={zone.id} className={`${zone.bg} grid items-stretch`} style={{gridTemplateColumns: '3fr 4fr'}}>
            {/* Left: zone label + description */}
            <div className={`px-5 py-4 border-b md:border-b-0 md:border-r ${zone.border} flex flex-col justify-center`}>
              <div className={`text-sm font-bold tracking-widest uppercase ${zone.color}`}>{zone.label}</div>
              <div className={`text-base font-semibold ${zone.color} mb-2`}>{zone.sublabel}</div>
              <p className={`text-sm ${zone.color} leading-relaxed`}>{zone.desc}</p>
            </div>
            {/* Right: stacked level cards — no gaps, continuous */}
            <div className="divide-y divide-white divide-opacity-60">
              {LEVELS.filter(l => l.zone === zone.id).map(level => (
                <div key={level.id}
                  className={`${level.bg} px-4 py-3 flex items-center gap-3`}>
                  <div className={`w-6 h-6 rounded-full ${level.pill} flex items-center justify-center text-white text-xs font-bold flex-shrink-0`}>
                    {level.id}
                  </div>
                  <div>
                    <div className={`font-bold text-sm ${level.text} flex items-center gap-1.5`}>
                      {level.icon}
                      <span>{level.title}</span>
                    </div>
                    <p className={`text-xs ${level.text} opacity-75 mt-0.5`}>{level.tagline}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* ── The Reflexive Loop ──────────────────────────────────────────────── */}
      <div className="bg-[var(--coral-dim)] border-2 border-[var(--coral)] p-6">
        <div className="flex items-start gap-3 mb-4">
          <RotateCcw className="h-6 w-6 text-[var(--coral)] flex-shrink-0 mt-0.5" />
          <div>
            <h2 className="text-lg font-bold text-[var(--coral)]">The Reflexive Loop — Argyris' Critical Insight</h2>
            <p className="text-sm text-[var(--coral)] mt-1 leading-relaxed">
              The descent isn't linear — it's circular. Once you reach Level B5 (Believe),
              your beliefs travel <em>back up</em> to Level B1 (Select) and determine what
              data you notice next. You look for confirmation. You find it. The basement
              starts shaping what you see on the ground floor.
            </p>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-[var(--coral-dim)] p-3 border border-[var(--coral)]">
            <div className="text-xs font-bold text-[var(--coral)] mb-1">The Loop Compounding</div>
            <p className="text-xs text-[var(--coral)]">
              "Marcus is skeptical of me" → I notice every silence →
              I stop noticing support → belief strengthens → I route around him →
              he notices the distance → now he becomes guarded → repeat.
            </p>
          </div>
          <div className="bg-[var(--coral-dim)] p-3 border border-[var(--coral)]">
            <div className="text-xs font-bold text-[var(--coral)] mb-1">The Intervention</div>
            <p className="text-xs text-[var(--coral)]">
              "What data would change my mind? Am I actively looking for it?
              What has happened recently that I've dismissed or explained away?"
            </p>
          </div>
          <div className="bg-[var(--coral-dim)] p-3 border border-[var(--coral)]">
            <div className="text-xs font-bold text-[var(--coral)] mb-1">Argyris Called This</div>
            <p className="text-xs text-[var(--coral)]">
              <strong>Single-loop:</strong> acting from belief, fixing the action.
              <strong> Double-loop:</strong> questioning the belief itself.
              The Inference Descent enables double-loop learning by making the loop visible.
            </p>
          </div>
        </div>
      </div>

      {/* ── The Drift vs The Return ─────────────────────────────────────────── */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-[var(--mustard-dim)] border-2 border-[var(--mustard)] p-5">
          <div className="flex items-center gap-2 mb-3">
            <ArrowDown className="h-5 w-5 text-[var(--mustard)]" />
            <h3 className="font-bold text-[var(--mustard)]">The Drift — Automatic</h3>
          </div>
          <p className="text-sm text-[var(--mustard)] leading-relaxed mb-3">
            The descent from ground floor to basement takes milliseconds. It's mostly
            unconscious — a cognitive efficiency that works well for routine situations
            and fails dangerously when human relationships are involved.
          </p>
          <div className="flex flex-wrap items-center gap-1 text-xs text-[var(--mustard)] bg-[var(--mustard-dim)] rounded px-2 py-1.5">
            <span className="font-bold">Data</span>
            <ArrowRight className="h-3 w-3 flex-shrink-0" />
            <span>Select</span>
            <ArrowRight className="h-3 w-3 flex-shrink-0" />
            <span>Interpret</span>
            <ArrowRight className="h-3 w-3 flex-shrink-0" />
            <span>Assume</span>
            <ArrowRight className="h-3 w-3 flex-shrink-0" />
            <span>Conclude</span>
            <ArrowRight className="h-3 w-3 flex-shrink-0" />
            <span>Believe</span>
            <ArrowRight className="h-3 w-3 flex-shrink-0" />
            <span className="font-bold">Act</span>
          </div>
        </div>
        <div className="bg-[var(--slate-dim)] border-2 border-[var(--slate)] p-5">
          <div className="flex items-center gap-2 mb-3">
            <ArrowUp className="h-5 w-5 text-[var(--text-ter)]" />
            <h3 className="font-bold text-[var(--text-sec)]">The Return — Deliberate</h3>
          </div>
          <p className="text-sm text-[var(--text-sec)] leading-relaxed mb-3">
            Argyris called this <strong>"reflection in action"</strong> — deliberately
            climbing back toward the surface. The Return requires willingness to be wrong,
            genuine curiosity, and a gap between stimulus and response.
          </p>
          <div className="flex flex-wrap items-center gap-1 text-xs text-[var(--text-sec)] bg-[var(--slate-dim)] rounded px-2 py-1.5">
            <span className="font-bold">Act</span>
            <ArrowRight className="h-3 w-3 rotate-180 flex-shrink-0" />
            <span>Believe</span>
            <ArrowRight className="h-3 w-3 rotate-180 flex-shrink-0" />
            <span>Conclude</span>
            <ArrowRight className="h-3 w-3 rotate-180 flex-shrink-0" />
            <span className="font-bold">What did I actually see?</span>
          </div>
        </div>
      </div>

      {/* ── Reading this alone? ─────────────────────────────────────────────── */}
      <div className="bg-[var(--bg-raised)] border-2 border-consulting-slate-blue-200 overflow-hidden">
        <div className="bg-consulting-slate-blue-700 px-6 py-4">
          <h2 className="text-lg font-bold text-white">Reading this on your own? Start here.</h2>
          <p className="text-sm text-consulting-slate-blue-200 mt-1">
            This page is designed to replace a coaching session — or prepare you for one.
            Read it with a real situation in mind.
          </p>
        </div>
        <div className="p-6 space-y-4 text-sm text-charcoal-700 leading-relaxed">
          <p>
            Pick something that's bothering you right now. A relationship that's tense. A
            decision that feels stuck. Someone whose behavior seems inexplicable. A conflict
            you keep having. Hold that situation as you read.
          </p>
          <p>
            By the end of this page, you'll be able to trace exactly where your reasoning
            drifted from <em>what actually happened</em> to <em>what you're now doing about it</em> —
            and you'll find at least one level where you can make a different choice.
          </p>
          <div className="bg-consulting-slate-blue-50 border border-consulting-slate-blue-200 p-4">
            <p className="font-semibold text-consulting-slate-blue-900 mb-1">The one thing to remember:</p>
            <p className="text-consulting-slate-blue-800">
              Your reasoning is internally consistent. It always is. The descent doesn't reveal
              that you're wrong — it reveals <em>where</em> you added meaning that wasn't
              in the data. That's where the choice lives.
            </p>
          </div>
        </div>
      </div>

      {/* ── Worked Example ─────────────────────────────────────────────────── */}
      <div className="bg-[var(--bg-raised)] border-2 border-[var(--rule)] overflow-hidden">
        <div className="bg-charcoal-900 px-6 py-4">
          <h2 className="text-lg font-bold text-white">Worked Example — Tracing a Full Descent</h2>
          <p className="text-sm text-[var(--text-ter)] mt-0.5">
            A realistic scenario traced level by level. Watch for the moment interpretation becomes fact.
          </p>
        </div>
        <div className="p-6 space-y-2">
          <div className="bg-[var(--surface)] border border-[var(--rule)] p-4 mb-4">
            <p className="text-xs font-bold text-[var(--text-ter)] uppercase tracking-wide mb-1">The Situation</p>
            <p className="text-sm text-charcoal-700">
              Tuesday standup. You describe a revised approach to the client handoff process.
              When you finish, Marcus says nothing. He glances at his phone.
              The meeting moves on.
            </p>
          </div>
          {[
            {
              id: 1, floor: 'Ground Floor', color: 'border-[var(--slate)] bg-[var(--slate-dim)]', tc: 'text-[var(--text-sec)]', pill: 'bg-[var(--slate)]',
              content: <>Marcus said nothing after you described the handoff approach. He glanced at his phone. Two seconds. The meeting moved on. <span className="block mt-1 text-xs text-[var(--text-ter)] font-medium">This is all that actually happened. Everything below is added by you.</span></>,
            },
            {
              id: 2, floor: 'Level B1', color: 'border-[var(--slate)] bg-[var(--slate-dim)]', tc: 'text-[var(--text-sec)]', pill: 'bg-[var(--slate)]',
              content: <>You notice <em>the silence</em> and the phone glance. You don't notice that two other teammates also said nothing, or that Marcus gave a small nod while you were speaking. <span className="block mt-1 text-xs text-[var(--mustard)] font-medium">⟳ Reflexive loop: if you already believe Marcus is skeptical of you, you'll select data that confirms it.</span></>,
            },
            {
              id: 3, floor: 'Level B2', color: 'border-[var(--green)] bg-[var(--green-dim)]', tc: 'text-[var(--green)] dark:text-[var(--text-ter)]', pill: 'bg-[var(--green)]',
              content: <>You interpret the silence as disapproval. A colleague reading the same scene might call it distraction, or just how Marcus listens. <span className="block mt-1 text-xs text-[var(--green)] dark:text-[var(--text-ter)] font-medium">Neither reading is in the data. Both feel like seeing the situation clearly.</span></>,
            },
            {
              id: 4, floor: 'Level B3', color: 'border-[var(--green)] bg-[var(--green-dim)]', tc: 'text-[var(--green)] dark:text-[var(--text-ter)]', pill: 'bg-[var(--green)]',
              content: <>You assume silence = disagreement — a rule you built working with a previous manager who always spoke up immediately when he liked something. If he was on board, you knew it instantly. You've carried that pattern here without declaring it.</>,
            },
            {
              id: 5, floor: 'Level B4', color: 'border-[var(--mustard)] bg-[var(--mustard-dim)]', tc: 'text-[var(--mustard)]', pill: 'bg-[var(--mustard)]',
              content: <><em>"Marcus doesn't support this direction."</em> Feels obvious — follows logically from B1–B3. But B1–B3 were all added by you. <span className="block mt-1 text-xs text-[var(--mustard)] font-medium">The inference gap between a two-second phone glance and this conclusion is enormous.</span></>,
            },
            {
              id: 6, floor: 'Level B5', color: 'border-[var(--mustard)] bg-[var(--mustard-dim)]', tc: 'text-[var(--mustard)]', pill: 'bg-[var(--mustard)]',
              content: <><em>"Marcus has been quietly skeptical of my judgment for months."</em> This belief now travels back up to Level B1. Next time he's quiet in a meeting, you notice it immediately. The one time he pushes back on anything, it confirms everything.</>,
            },
            {
              id: 7, floor: 'The Basement', color: 'border-[var(--coral)] bg-[var(--coral-dim)]', tc: 'text-[var(--coral)]', pill: 'bg-[var(--coral)]',
              content: <>You stop looping Marcus into early-stage thinking. You route conversations around him. You tell a peer that "Marcus tends to resist change." The team dynamic starts to shift — and Marcus has no idea why. All completely logical — <em>given the belief.</em> The belief was built on a phone glance.</>,
            },
          ].map(({ id, floor: _floor, color, tc, pill, content }) => (
            <div key={id} className={`${color} border-l-4  p-4 flex gap-3`}>
              <div className={`w-7 h-7 rounded-full ${pill} flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-0.5`}>
                {id}
              </div>
              <div>
                <div className={`text-xs font-bold uppercase tracking-wide ${tc} mb-1`}>
                  {LEVELS[id - 1].title}
                </div>
                <div className="text-sm text-charcoal-700 leading-relaxed">{content}</div>
              </div>
            </div>
          ))}

          {/* The Return */}
          <div className="mt-4 bg-[var(--slate)] p-5 text-white">
            <div className="flex items-center gap-2 mb-3">
              <ArrowUp className="h-5 w-5 text-[var(--text-ter)]" />
              <span className="font-bold">The Return. One question changes everything.</span>
            </div>
            <p className="text-sm text-[var(--text-ter)] leading-relaxed mb-3">
              You're in the basement — routing around Marcus, quietly reframing him to peers,
              pulling back. Before you act further, ask the one return question:
            </p>
            <div className="bg-[var(--bg-raised)] bg-opacity-10 p-3 mb-3">
              <p className="text-white font-medium italic">
                "What did I actually see? Not what I concluded — what did I literally observe?"
              </p>
            </div>
            <p className="text-sm text-[var(--text-ter)] leading-relaxed">
              The answer: Marcus didn't speak after you shared your idea. He glanced at his phone.
              That's the entire ground floor. Everything else — the pattern, the skepticism,
              the belief he's been quietly undermining you — was added between B1 and B5.
              The repair takes one sentence: <strong className="text-white">"Hey Marcus, I didn't get a read from you in standup — what are your thoughts on the handoff approach?"</strong>{' '}
              That question closes the inference gap that a week of routing around him would have turned into a real fracture.
            </p>
          </div>
        </div>
      </div>

      {/* ── The Dual Descent ────────────────────────────────────────────────── */}
      <div className="bg-[var(--bg-raised)] border-2 border-[var(--slate)] overflow-hidden">
        <div className="bg-gradient-to-r from-purple-700 to-charcoal-900 px-6 py-4">
          <h2 className="text-lg font-bold text-white">The Dual Descent — Why You Get the Opposite of What You Want</h2>
          <p className="text-sm text-[var(--text-ter)] mt-1">
            Your basement actions become <em>someone else's observable data</em>. They start their own descent — about you. Two people, two basements, both completely certain. Neither talking.
          </p>
        </div>
        <div className="p-6">
          {/* Side-by-side descents */}
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            {/* Your descent */}
            <div className="space-y-1.5">
              <div className="text-xs font-bold text-[var(--text-ter)] uppercase tracking-widest mb-2 flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-[var(--slate)] text-white flex items-center justify-center text-xs font-bold">Y</span>
                Your Descent
              </div>
              {[
                { step: 'Observable Data', label: 'Marcus says nothing in standup', color: 'bg-[var(--slate-dim)] border-[var(--slate)] text-[var(--text-sec)]' },
                { step: 'Select', label: 'You focus on the silence', color: 'bg-[var(--slate-dim)] border-[var(--slate)] text-[var(--text-sec)]' },
                { step: 'Interpret', label: '"Silence means disapproval"', color: 'bg-[var(--green-dim)] border-[var(--green)] text-[var(--green)] dark:text-[var(--text-ter)]' },
                { step: 'Assume', label: '"Silence = disagreement"', color: 'bg-[var(--green-dim)] border-[var(--green)] text-[var(--green)] dark:text-[var(--text-ter)]' },
                { step: 'Conclude', label: '"Marcus doesn\'t support this"', color: 'bg-[var(--mustard-dim)] border-[var(--mustard)] text-[var(--mustard)]' },
                { step: 'Believe', label: '"He\'s been skeptical for months"', color: 'bg-[var(--mustard-dim)] border-[var(--mustard)] text-[var(--mustard)]' },
                { step: 'Act', label: 'You route around Marcus. Stop sharing work. Tell peers he resists change.', color: 'bg-[var(--coral-dim)] border-[var(--coral)] text-[var(--coral)] font-semibold' },
              ].map(({ step, label, color }) => (
                <div key={step} className={`${color} border  px-3 py-2 text-xs`}>
                  <span className="font-semibold mr-1.5">{step}:</span>{label}
                </div>
              ))}
            </div>
            {/* Marcus's descent */}
            <div className="space-y-1.5">
              <div className="text-xs font-bold text-[var(--text-ter)] uppercase tracking-widest mb-2 flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-[var(--slate)] text-white flex items-center justify-center text-xs font-bold">M</span>
                Marcus's Descent — triggered by your actions
              </div>
              {[
                { step: 'Observable Data', label: 'You stop including Marcus. Look past him in the hall.', color: 'bg-[var(--slate-dim)] border-[var(--slate)] text-[var(--text-sec)]' },
                { step: 'Select', label: 'Marcus notices the exclusion', color: 'bg-[var(--slate-dim)] border-[var(--slate)] text-[var(--text-sec)]' },
                { step: 'Interpret', label: '"She\'s withdrawing from me"', color: 'bg-[var(--slate-dim)] border-[var(--slate)] text-[var(--text-sec)]' },
                { step: 'Assume', label: '"People withdraw when they\'ve lost confidence"', color: 'bg-[var(--slate-dim)] border-[var(--slate)] text-[var(--text-sec)]' },
                { step: 'Conclude', label: '"She\'s lost confidence in my judgment"', color: 'bg-[var(--slate-dim)] border-[var(--slate)] text-[var(--text-sec)]' },
                { step: 'Believe', label: '"I\'m seen as resistant to change on this team"', color: 'bg-[var(--green-dim)] border-[var(--green)] text-[var(--green)] dark:text-[var(--text-ter)]' },
                { step: 'Act', label: 'Marcus becomes guarded. Stops engaging. Starts protecting himself. Becomes — actually — harder to reach.', color: 'bg-[var(--coral-dim)] border-[var(--coral)] text-[var(--coral)] font-semibold' },
              ].map(({ step, label, color }) => (
                <div key={step} className={`${color} border  px-3 py-2 text-xs`}>
                  <span className="font-semibold mr-1.5">{step}:</span>{label}
                </div>
              ))}
            </div>
          </div>

          {/* The loop closing */}
          <div className="bg-charcoal-900 p-5 mb-4">
            <div className="text-sm font-bold text-[var(--mustard)] mb-2">The Loop Closes — And Confirms Everything</div>
            <p className="text-sm text-[var(--text-ter)] leading-relaxed">
              Marcus's basement behavior — becoming guarded, harder to reach, pulling back —
              is now <strong className="text-white">new observable data for you.</strong>{' '}
              You notice it. You select it. You interpret it as more evidence he's resistant.
              Your belief gets stronger. His gets stronger. The relationship fractures —
              and nobody knows why, because the ground floor event was a two-second phone glance
              in a standup neither of you remembers clearly.
            </p>
          </div>

          {/* The intervention */}
          <div className="bg-[var(--slate-dim)] border-2 border-[var(--slate)] p-5">
            <div className="text-sm font-bold text-[var(--text-sec)] mb-2 flex items-center gap-2">
              <ArrowUp className="h-4 w-4" /> The one move that breaks both descents simultaneously
            </div>
            <p className="text-sm text-[var(--text-sec)] leading-relaxed mb-3">
              One question, asked from the ground floor, surfaces both descents at once:
            </p>
            <div className="bg-[var(--bg-raised)] border border-[var(--slate)] p-3 mb-3">
              <p className="text-sm text-charcoal-800 font-medium italic">
                "Hey Marcus — I want to make sure I'm reading this right. I didn't get a read from you in standup on the handoff approach. What are your actual thoughts?"
              </p>
            </div>
            <p className="text-xs text-[var(--text-sec)] leading-relaxed">
              That question is only possible from the ground floor. From the basement, you don't ask —
              you already know. That's the trap. The question isn't weakness;
              it's the only thing that can interrupt two simultaneous descents before they become permanent.
            </p>
          </div>
        </div>
      </div>

      {/* ── Try It Yourself ─────────────────────────────────────────────────── */}
      <div className="bg-[var(--bg-raised)] border-2 border-[var(--green)] overflow-hidden">
        <div className="bg-gradient-to-r from-teal-600 to-sky-700 px-6 py-4">
          <h2 className="text-lg font-bold text-white">Try It Yourself — Right Now</h2>
          <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)] mt-0.5">
            Pick the situation you had in mind when you started reading. Work through each
            level in order. Don't rush the Ground Floor.
          </p>
        </div>
        <div className="p-6 space-y-3">
          {[
            {
              id: 1, label: 'Observable Data — Ground Floor',
              color: 'bg-[var(--slate-dim)] border-[var(--slate)]',
              prompt: 'Write down only what you actually observed. Describe it as if to a camera. No interpretations, no emotions, no "because." Just: what happened, what was said, what was the timestamp.',
              example: 'Not: "She was dismissive." → Instead: "She replied with two sentences and didn\'t reference any of the three points I raised."',
            },
            {
              id: 2, label: 'Select — Level B1',
              color: 'bg-[var(--slate-dim)] border-[var(--slate)]',
              prompt: 'What data are you focusing on? What are you not focusing on? List both. Be honest about what you\'re ignoring or explaining away.',
              example: 'Focusing on: the shortness of the reply. Not focusing on: that she replied same day, or that she didn\'t say no.',
            },
            {
              id: 3, label: 'Interpret — Level B2',
              color: 'bg-[var(--green-dim)] border-[var(--green)]',
              prompt: 'What meaning are you adding? State it clearly. Then ask: could someone else look at the exact same data and add different meaning?',
              example: '"I\'m reading it as irritation." — Could someone read it as efficiency? Distraction? Professionalism? Yes.',
            },
            {
              id: 4, label: 'Assume — Level B3',
              color: 'bg-[var(--green-dim)] border-[var(--green)]',
              prompt: 'What are you assuming about context, intent, or what this means about the relationship? Where did this assumption come from — past experience, a rule you hold?',
              example: '"I\'m assuming short replies mean low interest. I formed this with my previous manager, who was very effusive when engaged."',
            },
            {
              id: 5, label: 'Conclude — Level B4',
              color: 'bg-[var(--mustard-dim)] border-[var(--mustard)]',
              prompt: 'State your conclusion plainly. Don\'t soften it — state it the way you\'re actually thinking it, not the polite version.',
              example: '"She doesn\'t respect my judgment." or "This project is going to get killed." or "He was doing this on purpose."',
            },
            {
              id: 6, label: 'Believe — Level B5',
              color: 'bg-[var(--mustard-dim)] border-[var(--mustard)]',
              prompt: 'Has this situation reinforced a belief you already held? What is that belief? Is this situation evidence for the belief — or is the belief causing you to interpret the situation as evidence?',
              example: '"I\'ve believed for six months that she doesn\'t take me seriously. Each incident makes the belief feel more confirmed."',
            },
            {
              id: 7, label: 'Act — The Basement',
              color: 'bg-[var(--coral-dim)] border-[var(--coral)]',
              prompt: 'What are you about to do — or already doing — as a result of this belief? Be specific about the action, not just the feeling.',
              example: '"I\'m going to stop cc\'ing her on updates. I\'ve started venting to my peers. I\'m planning to raise this with HR."',
            },
          ].map(({ id, label, color, prompt, example }) => (
            <div key={id}
              className={`${color} border  overflow-hidden`}>
              <button
                onClick={() => toggleExpanded(id)}
                className={`w-full px-4 py-3 flex items-center gap-3 text-left ${color}`}
              >
                <div className="w-7 h-7 rounded-full bg-charcoal-800 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                  {id}
                </div>
                <span className="font-semibold text-charcoal-900 text-sm flex-1">{label}</span>
                {expanded.has(id)
                  ? <ArrowDown className="h-4 w-4 text-charcoal-500" />
                  : <ChevronRight className="h-4 w-4 text-charcoal-400" />}
              </button>
              {expanded.has(id) && (
                <div className="px-5 pb-4">
                  <p className="text-sm text-charcoal-700 leading-relaxed mb-2">{prompt}</p>
                  <div className="bg-[var(--bg-raised)] bg-opacity-70 px-3 py-2 border border-white">
                    <p className="text-xs text-charcoal-500 italic">{example}</p>
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Descent summary */}
          <div className="bg-charcoal-900 p-5 mt-2">
            <div className="flex items-center gap-2 mb-3">
              <ArrowUp className="h-5 w-5 text-[var(--mustard)]" />
              <span className="font-bold text-white">Now: The Return to Surface</span>
            </div>
            <p className="text-sm text-[var(--text-ter)] leading-relaxed mb-3">
              You've traced your descent. Now return to the Ground Floor and ask:
              how far did you travel from the observable data to your conclusion?
              That distance is your inference gap. The wider it is, the more important
              these questions become:
            </p>
            <div className="bg-[var(--bg-raised)] bg-opacity-10 p-4 space-y-2">
              <p className="text-sm text-white font-medium italic">
                "What would I do differently if I tested my interpretation before acting on it?"
              </p>
              <p className="text-sm text-white font-medium italic">
                "What's the one question I could ask the other person that would either confirm or collapse my conclusion?"
              </p>
              <p className="text-sm text-white font-medium italic">
                "If my belief at B5 is wrong, what's the cost of the actions I'm taking from the basement?"
              </p>
            </div>
            <p className="text-xs text-[var(--text-ter)] mt-3 leading-relaxed">
              Most people, once they see the gap clearly, know exactly what to do.
              The goal of the first return isn't resolution — it's visibility.
            </p>
          </div>
        </div>
      </div>

      {/* ── Inquiry Questions ──────────────────────────────────────────────── */}
      <div className="bg-[var(--bg-raised)] border-2 border-[var(--rule)] overflow-hidden">
        <div className="bg-gradient-to-r from-sky-50 to-teal-50 px-6 py-4 border-b border-[var(--rule)]">
          <h2 className="text-lg font-bold text-charcoal-900">Return Questions — One Per Level</h2>
          <p className="text-sm text-charcoal-600 mt-0.5">
            Argyris' core tool for the return to surface. Ask backwards from wherever you are.
          </p>
        </div>
        <div className="divide-y divide-[var(--rule)]">
          {LEVELS.map(level => (
            <div key={level.id} className={`flex items-start gap-4 px-5 py-3`}>
              <div className={`w-7 h-7 rounded-full ${level.pill} flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-0.5`}>
                {level.id}
              </div>
              <div className="flex-1">
                <span className={`text-sm font-bold ${level.text}`}>{level.title}</span>
                <p className="text-sm text-charcoal-700 italic mt-0.5">"{level.inquiry}"</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── When to Use ───────────────────────────────────────────────────── */}
      <div className="bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-6">
        <h2 className="text-lg font-bold text-charcoal-900 mb-4">When to Use It</h2>
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { ctx: '🎯 Coaching', color: 'border-[var(--slate)] bg-[var(--slate-dim)]', desc: 'When a client is stuck in a recurring pattern. Trace the descent level by level. Ask: "Walk me back to the ground floor — what did you actually see?"' },
            { ctx: '⚡ Conflict', color: 'border-[var(--coral)] bg-[var(--coral-dim)]', desc: 'Two people have incompatible accounts of the same event. Each descent is internally consistent. The divergence is at B1 (Select) or B2 (Interpret), not the ground floor.' },
            { ctx: '🧭 Decisions', color: 'border-[var(--mustard)] bg-[var(--mustard-dim)]', desc: 'Before a high-stakes choice. "What level am I on right now? What\'s the gap between my observable data and this conclusion? What would change my mind?"' },
            { ctx: '🔁 Team Retrospectives', color: 'border-[var(--green)] bg-[var(--green-dim)]', desc: 'After a conflict or project. Map each person\'s descent for the same event. The divergence usually appears at B1 or B2 — not the basement where the argument is happening.' },
            { ctx: '💬 Communication', color: 'border-consulting-slate-blue-200 bg-consulting-slate-blue-50', desc: 'Before sending a charged message. "What level am I writing from — the ground floor or the basement? What would I say if I started from observable data only?"' },
            { ctx: '🌱 Self-reflection', color: 'border-[var(--mustard)] bg-[var(--mustard-dim)]', desc: 'When a belief about yourself or others feels absolute. Trace the descent. How deep is the inference gap? Was the ground floor as damning as you believed?' },
          ].map(({ ctx, color, desc }) => (
            <div key={ctx} className={` p-4 border ${color}`}>
              <div className="font-semibold text-charcoal-900 text-sm mb-1">{ctx}</div>
              <p className="text-xs text-charcoal-600 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Attribution ───────────────────────────────────────────────────── */}
      <div className="bg-charcoal-900 p-6 text-white">
        <h2 className="text-base font-bold text-[var(--mustard)] mb-4 uppercase tracking-wide">Origin &amp; Attribution</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <div className="text-sm font-bold text-white mb-2">Chris Argyris (1923–2013)</div>
            <p className="text-xs text-[var(--text-ter)] leading-relaxed">
              Harvard Business School professor and organizational theorist. Developed the Ladder
              of Inference as part of his work on <strong className="text-[var(--text-ter)]">Action Science</strong> —
              the study of how people actually reason and act in organizations. His core insight:
              the gap between our "espoused theory" and our "theory in use" is where
              organizational problems live. The reflexive loop was his most important contribution —
              without it, the model is merely descriptive.
            </p>
            <div className="mt-3 space-y-1 text-xs text-[var(--text-ter)]">
              <p><span className="text-[var(--mustard)] font-medium">Key work:</span> <em>Reasoning, Learning, and Action</em> (1982)</p>
              <p><span className="text-[var(--mustard)] font-medium">Key concept:</span> Double-loop learning — questioning assumptions, not just fixing actions</p>
              <p><span className="text-[var(--mustard)] font-medium">Collaborator:</span> Donald Schön (<em>The Reflective Practitioner</em>)</p>
            </div>
          </div>
          <div>
            <div className="text-sm font-bold text-white mb-2">The Fifth Discipline (Peter Senge, 1990)</div>
            <p className="text-xs text-[var(--text-ter)] leading-relaxed">
              Senge introduced the Ladder of Inference to a broader management audience as
              a core tool for the "Mental Models" discipline — one of the five disciplines
              of a learning organization. His framing made Argyris' academic work
              practical for practitioners and coaches worldwide.
            </p>
            <div className="mt-3 space-y-1 text-xs text-[var(--text-ter)]">
              <p><span className="text-[var(--mustard)] font-medium">Framing:</span> One of five disciplines — alongside Systems Thinking, Team Learning, Personal Mastery, Shared Vision</p>
              <p><span className="text-[var(--mustard)] font-medium">This page:</span> The Inference Descent</p>
            </div>
          </div>
        </div>
      </div>


    </div>
  );
};
